import {
  Box, CircularProgress, IconButton, Stack, Typography,
} from '@mui/material';
import React, { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { CancelOutlined } from '@mui/icons-material';
import { getFileExtensions } from '../../helpers';
import CustomButton from '../Buttons/CustomButton';
import { ReactComponent as DownloadIcon } from '../../assets/download.svg';

const containerSx = {
  border: '2px dashed rgba(25, 118, 210, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function DropZone({
  getFiles,
  sx,
  accept = getFileExtensions(),
  maxFiles = 1,
  withFiles = false,
  files,
  loading,
  name,
  onDelete,
  ...props
}) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      getFiles({ target: { name } }, acceptedFiles);
    },
    [getFiles, maxFiles, name],
  );
  const {
    getRootProps, getInputProps, isDragActive, isDragReject,
  } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    ...props,
  });
  const inputProps = getInputProps();
  const rootProps = loading ? {} : getRootProps();


  const inputRef = useRef(null);
  const { ref: dropzoneRef, ...restInputProps } = inputProps;
  const mergedRef = (node) => {
    inputRef.current = node;
    if (typeof dropzoneRef === 'function') dropzoneRef(node);
    else if (dropzoneRef && 'current' in dropzoneRef) dropzoneRef.current = node;
  };

  function truncate(str, maxLength = 30) {
    if (!str) return '';
    return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
  }

  return (
    <Stack gap="16px" direction="column">
      <Box
        sx={{
          ...containerSx, ...sx, minHeight: '150px', borderColor: isDragReject ? 'red' : '',
        }}
        {...rootProps}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current && inputRef.current.click();
        }}
        onKeyDown={() => {
        }}
        onFocus={() => {
        }}
      >
        {loading && <CircularProgress color="text" sx={{ display: 'flex', margin: '0 auto' }} />}
        {!loading && (
        <>
          <input {...restInputProps} ref={mergedRef} />
          {
            // eslint-disable-next-line no-nested-ternary
            isDragReject ? <Typography color="error">Неверный формат файла</Typography> : isDragActive
              ? <Typography>Прикрепить</Typography>
              : (
                <Stack
                  sx={{ padding: '32px' }}
                  gap="10px"
                  direction="column"
                  alignItems="center"
                >
                  <DownloadIcon />
                  <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                    Перетащите файл сюда
                  </Typography>
                  <Typography sx={{ fontSize: '14px' }}>
                    или
                  </Typography>
                  <CustomButton
                    sx={{ height: '30px' }}
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      inputRef.current && inputRef.current.click();
                    }}
                  >
                    Выберите файл
                  </CustomButton>
                </Stack>
              )
                  }
        </>
        )}
      </Box>
      {withFiles
        && (
        <Stack direction="column" gap="8px" sx={{ overflowY: 'auto' }} maxHeight="150px">
          {files.map((file) => (
            <Stack direction="row" gap="8px" alignItems="center" key={file.randId}>
              <Typography fontFamily="Inter" sx={{ color: 'rgba(57, 69, 83, 1)' }} fontWeight={500}>
                {truncate(file.name)}
                {' '}
                {(Math.ceil((file.size / (1024 * 1024)) * 100) / 100).toFixed(2)}
                {' '}
                МБ
              </Typography>
              <IconButton onClick={() => {
                if (onDelete) onDelete(file);
              }}
              >
                <CancelOutlined fontSize="24px" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        )}
    </Stack>
  );
}

export default DropZone;
